import './DodLayoutOptions.scss';
import classnames from 'classnames';
import { ByzzerChangeEvent, ByzzerChangeEventHandler, ByzzerTextTip, ByzzerTipIcon, ByzzerWindowedSelect } from '@byzzer/ui-components';
import {useEventDataWithUserInfo, useTrackEvent} from "@/analytics/AnalyticsContext";
import {DodLayoutConfig, DodRowDef} from '@/types/DodRun';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { useEffect, useMemo } from 'react';
import { isWorkInProgressFact, hasUPCorSharedFactSelected } from '@/components/DodConfigEditor/common/utils';

const baseClassName = 'dod-layout-options';

export type DodLayoutOptionsProps = {
    className?: string;
    name?: string;
    value: DodLayoutConfig;
    onChange: ByzzerChangeEventHandler<DodLayoutConfig>;
    mode: string | null;
};

const OPTION_INCLUDE_SUBTOTALS = 'includeSubTotals';
const OPTION_INCLUDE_CATEGORY_TOTALS = 'includeCategoryTotals';
const OPTION_INCLUDE_EXTRACT_DATE = 'includeExtractDate';
const OPTION_INCLUDE_NO_HISTORY_LABELS = 'includeNoHistoryLabels';
const OPTION_INCLUDE_GRANULARITY_LEVEL = 'includeGranularityLevel';
const OPTION_FILTER_NOT_APPLICABLE = 'filterNotApplicable';

function LayoutOption({ data, isDisabled }: any) {
    const infoTip = data?.data?.infoTip;
    const disabledTip = data?.data?.disabledTip;

    const content = (
        <div className={`${baseClassName}__option-content`}>
            <span>{data.display}</span>
            {Boolean(infoTip) && (
                <ByzzerTipIcon
                    tipDelay={[750, 0]}
                    tip={<p>{infoTip}</p>}
                    className={`${baseClassName}__option-tip`}
                />
            )}
        </div>
    );

    if (isDisabled && disabledTip) {
        return (
            <ByzzerTextTip tip={disabledTip} tipLocation={'right'} tipDelay={[500, 0]}>
                <span className={`${baseClassName}__option-wrapper`}>{content}</span>
            </ByzzerTextTip>
        );
    }

    return content;
}

export function DodLayoutOptions({ className, name, value, mode,  onChange, ...props }: DodLayoutOptionsProps) {
    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const isLayoutMode = mode === 'layout';
    const categoryTotalDisabledDueToHiding = 'You cannot include Category Totals when you hide Category from your run.';
    const categoryTotalDisabledDueToStacking = 'You cannot include Category Totals when you stack Category in your run.';
    const categoryTotalDisabledDueToTopLevelHierarchies = 'You must include product levels below Category to include Category Totals in your run.';
    const categoryTotalNullValuesTip = 'Some values will be null in the output file due to not being reportable at the Category level.';
    const filterNotApplicableDisabledTip = 'This option is only compatible with stacked products layouts. Check out the resource library for more information.';
    const { runConfig, rowColConfigs } = useDodWizard();
    const disabledByInProgressFacts = useMemo(() => {
        return isWorkInProgressFact(runConfig.facts);
    }, [runConfig.facts]);
    const isCategoryTotalsDisabledByHiddenCategory = isCategoryHidden();
    const isCategoryTotalsDisabledByStackedCategory = isCategoryStacked();
    const isCategoryTotalsDisabledByMissingHierarchy = !hasOnlyTopLevelHierarchies();
    const hasStackedProducts = value.rows.some((row) => row.type === 'products' && row.stack);
    const isFilterNotApplicableDisabled = !hasStackedProducts;
    const isCategoryTotalsDisabled =
        isLayoutMode ||
        isCategoryTotalsDisabledByHiddenCategory ||
        isCategoryTotalsDisabledByStackedCategory ||
        isCategoryTotalsDisabledByMissingHierarchy ||
        disabledByInProgressFacts.disabled;
    const categoryTotalsDisabledTip = disabledByInProgressFacts.disabled
        ? disabledByInProgressFacts.hoverText
        : isCategoryTotalsDisabledByHiddenCategory
        ? categoryTotalDisabledDueToHiding
        : isCategoryTotalsDisabledByStackedCategory
        ? categoryTotalDisabledDueToStacking
        : isCategoryTotalsDisabledByMissingHierarchy
        ? categoryTotalDisabledDueToTopLevelHierarchies
        : '';

    const selectedLayoutOptionCount = useMemo(() => {
        let selectedCount = 0;
        if (value.includeSubTotals) selectedCount += 1;
        if (value.includeCategoryTotals) selectedCount += 1;
        if (value.includeExtractDate) selectedCount += 1;
        if (value.includeNoHistoryLabels) selectedCount += 1;
        if (value.includeGranularityLevel) selectedCount += 1;
        if (value.filterNotApplicable) selectedCount += 1;
        return selectedCount;
    }, [value.includeCategoryTotals, value.includeExtractDate, value.includeSubTotals, value.includeNoHistoryLabels, value.includeGranularityLevel, value.filterNotApplicable]);

    const selectedLayoutOptionLabel = useMemo(() => {
        if (selectedLayoutOptionCount === 0) {
            return 'Add custom layout options';
        }

        if (selectedLayoutOptionCount > 1) {
            return 'Multiple layout options selected';
        }

        if (value.includeSubTotals) {
            return 'Include Subtotals';
        }

        if (value.includeCategoryTotals) {
            return 'Include Category Totals';
        }

        if (value.includeNoHistoryLabels) {
            return 'Include No History Labels';
        }

        if (value.includeGranularityLevel) {
            return 'Include Granularity Level';
        }

        if (value.filterNotApplicable) {
            return 'Filter out NOT APPLICABLE values';
        }

        return 'Extract Date';
    }, [selectedLayoutOptionCount, value.includeCategoryTotals, value.includeExtractDate, value.includeSubTotals, value.includeNoHistoryLabels, value.includeGranularityLevel, value.filterNotApplicable]);

    const disabledLayoutOptions = useMemo(() => {
        const disabledOptions: string[] = [];

        if (isLayoutMode || disabledByInProgressFacts.disabled) {
            disabledOptions.push(OPTION_INCLUDE_SUBTOTALS);
        }

        if (isLayoutMode) {
            disabledOptions.push(OPTION_INCLUDE_EXTRACT_DATE);
            disabledOptions.push(OPTION_INCLUDE_NO_HISTORY_LABELS);
            disabledOptions.push(OPTION_INCLUDE_GRANULARITY_LEVEL);
        }

        if (isCategoryTotalsDisabled) {
            disabledOptions.push(OPTION_INCLUDE_CATEGORY_TOTALS);
        }

        if (isFilterNotApplicableDisabled && !disabledOptions.includes(OPTION_FILTER_NOT_APPLICABLE)) {
            disabledOptions.push(OPTION_FILTER_NOT_APPLICABLE);
        }

        return disabledOptions;
    }, [disabledByInProgressFacts.disabled, isCategoryTotalsDisabled, isLayoutMode, isFilterNotApplicableDisabled, value.filterNotApplicable]);

    const layoutOptionsWithMeta = useMemo(() => {
        const subtotalsDisabledTip = disabledByInProgressFacts.disabled
            ? disabledByInProgressFacts.hoverText
            : '';

        return [
            {
                display: 'Include Subtotals',
                value: OPTION_INCLUDE_SUBTOTALS,
                data: { disabledTip: subtotalsDisabledTip },
            },
            {
                display: 'Include Category Totals',
                value: OPTION_INCLUDE_CATEGORY_TOTALS,
                data: {
                    disabledTip: categoryTotalsDisabledTip,
                    infoTip: hasUPCorSharedFactSelected(runConfig.facts) ? categoryTotalNullValuesTip : '',
                },
            },
            {
                display: 'Extract Date',
                value: OPTION_INCLUDE_EXTRACT_DATE,
                data: { disabledTip: '' },
            },
            {
                display: 'Include No History Labels',
                value: OPTION_INCLUDE_NO_HISTORY_LABELS,
                data: { disabledTip: '' },
            },
            {
                display: 'Include Granularity Level',
                value: OPTION_INCLUDE_GRANULARITY_LEVEL,
                data: { disabledTip: '' },
            },
            {
                display: 'Filter out NOT APPLICABLE values',
                value: OPTION_FILTER_NOT_APPLICABLE,
                data: { disabledTip: filterNotApplicableDisabledTip },
            },
        ];
    }, [categoryTotalsDisabledTip, categoryTotalNullValuesTip, disabledByInProgressFacts, runConfig.facts, filterNotApplicableDisabledTip]);

    const selectedLayoutOptions = useMemo(() => {
        const selectedOptions: string[] = [];
        if (value.includeSubTotals) selectedOptions.push(OPTION_INCLUDE_SUBTOTALS);
        if (value.includeCategoryTotals) selectedOptions.push(OPTION_INCLUDE_CATEGORY_TOTALS);
        if (value.includeExtractDate) selectedOptions.push(OPTION_INCLUDE_EXTRACT_DATE);
        if (value.includeNoHistoryLabels) selectedOptions.push(OPTION_INCLUDE_NO_HISTORY_LABELS);
        if (value.includeGranularityLevel) selectedOptions.push(OPTION_INCLUDE_GRANULARITY_LEVEL);
        if (value.filterNotApplicable) selectedOptions.push(OPTION_FILTER_NOT_APPLICABLE);
        return selectedOptions;
    }, [value.includeCategoryTotals, value.includeExtractDate, value.includeSubTotals, value.includeNoHistoryLabels, value.includeGranularityLevel, value.filterNotApplicable]);

    const selectorInstanceKey = useMemo(() => {
        return `layout-options-${disabledLayoutOptions.slice().sort().join('|') || 'none'}`;
    }, [disabledLayoutOptions]);

    function handleValueChange(e: ByzzerChangeEvent<any>) {
        const selectedOptions = Array.isArray(e.value)
            ? (e.value as string[])
            : typeof e.value === 'string'
            ? [e.value]
            : [];

        const shouldIncludeSubTotals =
            (isLayoutMode && value.includeSubTotals) || selectedOptions.includes(OPTION_INCLUDE_SUBTOTALS);
        const shouldIncludeCategoryTotals =
            (isLayoutMode && value.includeCategoryTotals) || selectedOptions.includes(OPTION_INCLUDE_CATEGORY_TOTALS);
        const shouldIncludeExtractDate =
            (isLayoutMode && value.includeExtractDate) || selectedOptions.includes(OPTION_INCLUDE_EXTRACT_DATE);
        const shouldIncludeNoHistoryLabels =
            (isLayoutMode && value.includeNoHistoryLabels) || selectedOptions.includes(OPTION_INCLUDE_NO_HISTORY_LABELS);
        const shouldIncludeGranularityLevel =
            (isLayoutMode && value.includeGranularityLevel) || selectedOptions.includes(OPTION_INCLUDE_GRANULARITY_LEVEL);
        const shouldFilterNotApplicable =
            (isLayoutMode && value.filterNotApplicable) || selectedOptions.includes(OPTION_FILTER_NOT_APPLICABLE);

        const nextValue = {
            ...value,
            includeSubTotals: shouldIncludeSubTotals,
            includeCategoryTotals: shouldIncludeCategoryTotals,
            includeExtractDate: shouldIncludeExtractDate,
            includeNoHistoryLabels: shouldIncludeNoHistoryLabels,
            includeGranularityLevel: shouldIncludeGranularityLevel,
            filterNotApplicable: shouldFilterNotApplicable,
        };

        const optionChanges = [
            { option: OPTION_INCLUDE_SUBTOTALS, changed: value.includeSubTotals !== nextValue.includeSubTotals },
            {
                option: OPTION_INCLUDE_CATEGORY_TOTALS,
                changed: value.includeCategoryTotals !== nextValue.includeCategoryTotals,
            },
            { option: OPTION_INCLUDE_EXTRACT_DATE, changed: value.includeExtractDate !== nextValue.includeExtractDate },
            { option: OPTION_INCLUDE_NO_HISTORY_LABELS, changed: value.includeNoHistoryLabels !== nextValue.includeNoHistoryLabels },
            {
                option: OPTION_INCLUDE_GRANULARITY_LEVEL,
                changed: value.includeGranularityLevel !== nextValue.includeGranularityLevel,
            },
            { option: OPTION_FILTER_NOT_APPLICABLE, changed: value.filterNotApplicable !== nextValue.filterNotApplicable },
        ].filter((item) => item.changed);

        if (!optionChanges.length) {
            return;
        }

        onChange({
            name,
            value: nextValue,
        });

        optionChanges.forEach((item) => {
            trackEvent({
                type: 'click',
                name: 'dod_layout_options_change',
                data: getEventData({ dodWizardStep: 'layout', option: item.option }),
            });
        });
    }

    function isCategoryHidden(): boolean {
        // disable categoryTotals if category is hidden in layout
        return [...value.columns, ...value.rows].some((axisDef) => axisDef.dim === 'categories' && axisDef.hide);
    }

    function isCategoryStacked(): boolean {
        // disable categoryTotals if category is stacked in layout
        return [...value.columns, ...value.rows].some((axisDef) => axisDef.dim === 'categories' && (axisDef as DodRowDef)?.stack);
    }

    function hasOnlyTopLevelHierarchies(): boolean {
        const productLevels = [...value.columns, ...value.rows].filter((axisDef) => axisDef.type === 'products')
        const nonTopLevelHierarchies = productLevels.filter((axisDef) => axisDef.dim !== 'categories' && axisDef.dim !== 'departments' && axisDef.dim !== 'superCategories');
        return Boolean(nonTopLevelHierarchies.length);
    }

    // BYZ-17271 auto uncheck includeCategoryTotals if no product levels below category are present and set catgegoryTotals to false
    // because it cannot be selected in that scenario
    useEffect(() => {
        const hasProductLevelsBelowCategory = rowColConfigs.some(
            (rcc) =>
                rcc.type === 'products' &&
                rcc.dim !== 'categories' &&
                rcc.dim !== 'departments' &&
                rcc.dim !== 'superCategories'
        );
        const shouldResetCategoryTotals = !isLayoutMode && !hasProductLevelsBelowCategory;

        if (shouldResetCategoryTotals) {
            onChange({
                name,
                value: { ...value, includeCategoryTotals: false }
            });
        }
    }, [isLayoutMode, rowColConfigs]);

    // Auto uncheck filterNotApplicable if no stacked products exist
    useEffect(() => {
        const shouldResetFilterNotApplicable =
            !isLayoutMode && !hasStackedProducts && value.filterNotApplicable;

        if (shouldResetFilterNotApplicable) {
            onChange({
                name,
                value: { ...value, filterNotApplicable: false }
            });
        }
    }, [isLayoutMode, hasStackedProducts, value.filterNotApplicable, onChange, name, value]);

    return (
        <div className={classnames(baseClassName, className)} {...props}>
            <span className={`${baseClassName}__title`}>Layout Options:</span>
            <div className={`${baseClassName}__selector`}>
                <ByzzerWindowedSelect
                    key={selectorInstanceKey}
                    name={name}
                    value={selectedLayoutOptions}
                    onChange={handleValueChange}
                    options={layoutOptionsWithMeta}
                    disabledOptions={disabledLayoutOptions}
                    optionComponent={LayoutOption}
                    allowMultiple={true}
                    allowClear={false}
                    placeholder={selectedLayoutOptionLabel}
                    reactSelectProps={{
                        controlShouldRenderValue: true,
                        hideSelectedOptions: false,
                        isSearchable: false,
                    }}
                />
            </div>
        </div>
    );
}

export default DodLayoutOptions;
